import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { formatCourtLineSummary, getCourtStateLabel } from "@bw/shared/utils";

import { searchPlayers, type PlayerSearchResult } from "@/libs/frontDeskApi";
import { getCourtBoard } from "@/libs/facilityApi";
import {
  listPartyMemberships,
  requeuePartyForStaff,
  removePartyMembership,
  type PartyMembershipRecovery,
} from "@/libs/queueApi";

const REASON_LABELS: Record<string, string> = {
  duplicate_active_membership: "Duplicate active membership",
  missing_active_admission: "Missing active check-in",
  stale_party_status: "Stale party status",
};

function safeName(
  displayName: string | null | undefined,
  userId: string,
): string {
  const name = displayName?.trim();
  return name && name !== userId ? name : "Unnamed player";
}

function formatTimestamp(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function partyStatus(status: string): string {
  switch (status) {
    case "open":
      return "Open";
    case "queued":
      return "Queued";
    case "playing":
      return "Playing";
    default:
      return "Active";
  }
}

function linkage(membership: PartyMembershipRecovery): string {
  if (membership.assignment_id) {
    const courtName = membership.court_name?.trim();
    return courtName ? `On court: ${courtName}` : "Assigned to a court";
  }
  switch (membership.entry_status) {
    case "waiting":
      return "Queue: Waiting";
    case "assigned":
      return "Queue: Assigned";
    default:
      return "Not in queue";
  }
}

function membershipContext(membership: PartyMembershipRecovery): string {
  const roster = membership.member_details
    .map((member) => safeName(member.display_name, member.user_id))
    .join(", ");
  const createdAt = formatTimestamp(membership.party_created_at);
  const joinedAt = formatTimestamp(membership.membership_joined_at);
  const completedAt = formatTimestamp(membership.completed_session_ended_at);
  const timestamps = [
    createdAt ? `Created ${createdAt}.` : null,
    joinedAt && joinedAt !== createdAt ? `Joined ${joinedAt}.` : null,
    completedAt ? `Court session ended ${completedAt}.` : null,
  ]
    .filter(Boolean)
    .join(" ");

  const rosterContext = roster
    ? `Party members: ${roster}.`
    : "Party roster unavailable.";
  return `${rosterContext}${timestamps ? ` ${timestamps}` : ""}`;
}

export function PartyMembershipRecovery({
  facilityId,
}: {
  facilityId: string;
}) {
  return (
    <PartyMembershipRecoveryFacility key={facilityId} facilityId={facilityId} />
  );
}

function PartyMembershipRecoveryFacility({
  facilityId,
}: {
  facilityId: string;
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedPlayer, setSelectedPlayer] =
    useState<PlayerSearchResult | null>(null);
  const [pendingRemoval, setPendingRemoval] =
    useState<PartyMembershipRecovery | null>(null);
  const [removalError, setRemovalError] = useState<string | null>(null);
  const [requeueErrorPartyId, setRequeueErrorPartyId] = useState<string | null>(
    null,
  );
  const [pendingRequeue, setPendingRequeue] =
    useState<PartyMembershipRecovery | null>(null);
  const [targetCourtId, setTargetCourtId] = useState("");
  const [courtPickerOpen, setCourtPickerOpen] = useState(false);

  const trimmedSearch = search.trim();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(trimmedSearch);
    }, 200);
    return () => window.clearTimeout(timer);
  }, [trimmedSearch]);

  const playersQuery = useQuery({
    queryKey: ["drop-in", "party-player-search", facilityId, debouncedSearch],
    queryFn: () => searchPlayers(facilityId, debouncedSearch),
    enabled: debouncedSearch.length >= 2,
    retry: false,
  });

  const membershipsKey = [
    "drop-in",
    "party-memberships",
    facilityId,
    selectedPlayer?.user_id ?? null,
  ] as const;

  const membershipsQuery = useQuery({
    queryKey: membershipsKey,
    queryFn: () => listPartyMemberships(facilityId, selectedPlayer!.user_id),
    enabled: selectedPlayer !== null,
    retry: false,
  });

  const courtBoardQuery = useQuery({
    queryKey: ["drop-in", "party-requeue-courts", facilityId],
    queryFn: () => getCourtBoard(facilityId),
    enabled: pendingRequeue?.queue_assignment_mode === "self_serve",
    staleTime: 0,
    retry: false,
  });

  const cleanup = useMutation({
    mutationFn: (membership: PartyMembershipRecovery) =>
      removePartyMembership(membership.party_id, membership.subject_user_id),
    onSuccess: async () => {
      setPendingRemoval(null);
      setRemovalError(null);
      await queryClient.invalidateQueries({ queryKey: membershipsKey });
    },
    onError: (_error, membership) => {
      setRemovalError(
        membership.is_leader
          ? "We couldn't disband this party. Please try again."
          : "We couldn't remove this membership. Please try again.",
      );
    },
  });

  const requeue = useMutation({
    mutationFn: ({
      membership,
      courtId,
    }: {
      membership: PartyMembershipRecovery;
      courtId?: string;
    }) =>
      requeuePartyForStaff(membership.party_id, {
        mode: membership.requeue_mode,
        exclusive: membership.requeue_exclusive,
        ...(courtId ? { target_court_id: courtId } : {}),
      }),
    onSuccess: async () => {
      setRequeueErrorPartyId(null);
      setPendingRequeue(null);
      setTargetCourtId("");
      setCourtPickerOpen(false);
      await queryClient.invalidateQueries({ queryKey: membershipsKey });
    },
    onError: async (_error, variables) => {
      setRequeueErrorPartyId(variables.membership.party_id);
      setPendingRequeue(null);
      setTargetCourtId("");
      setCourtPickerOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: membershipsKey }),
        queryClient.invalidateQueries({
          queryKey: ["drop-in", "party-requeue-courts", facilityId],
        }),
      ]);
    },
  });

  const selectPlayer = (player: PlayerSearchResult) => {
    setSelectedPlayer(player);
    setPendingRemoval(null);
    setRemovalError(null);
    setRequeueErrorPartyId(null);
    setPendingRequeue(null);
    setTargetCourtId("");
    setCourtPickerOpen(false);
  };

  const changeSearch = (value: string) => {
    setSearch(value);
    setSelectedPlayer(null);
    setPendingRemoval(null);
    setRemovalError(null);
    setRequeueErrorPartyId(null);
    setPendingRequeue(null);
    setTargetCourtId("");
    setCourtPickerOpen(false);
    cleanup.reset();
    requeue.reset();
  };

  const closeDialog = () => {
    if (cleanup.isPending) return;
    setPendingRemoval(null);
    setRemovalError(null);
    cleanup.reset();
  };

  const confirmRemoval = () => {
    if (!pendingRemoval || cleanup.isPending) return;
    setRemovalError(null);
    cleanup.mutate(pendingRemoval);
  };

  const closeRequeueDialog = () => {
    if (requeue.isPending) return;
    setPendingRequeue(null);
    setTargetCourtId("");
    setCourtPickerOpen(false);
    setRequeueErrorPartyId(null);
    requeue.reset();
  };

  const memberships = membershipsQuery.data ?? [];
  const eligibleCourts = pendingRequeue
    ? (courtBoardQuery.data?.courts ?? []).filter(
        (court) =>
          court.availability_state !== "unavailable" &&
          court.capacity >= pendingRequeue.member_details.length,
      )
    : [];
  const selectedCourt = eligibleCourts.find(
    (court) => court.court_id === targetCourtId,
  );
  const courtOptionLabel = (court: (typeof eligibleCourts)[number]) =>
    `${court.name} · ${getCourtStateLabel({
      operationalState: court.operational_state,
      occupancyState: court.occupancy_state,
    })} · ${
      formatCourtLineSummary(court.line_group_count, court.line_player_count) ??
      "No one waiting"
    }`;

  useEffect(() => {
    if (targetCourtId && courtBoardQuery.data && !selectedCourt) {
      setTargetCourtId("");
    }
  }, [courtBoardQuery.data, selectedCourt, targetCourtId]);

  const confirmRequeue = () => {
    if (
      !pendingRequeue ||
      !selectedCourt ||
      requeue.isPending ||
      courtBoardQuery.isFetching ||
      courtBoardQuery.data?.queue_open === false
    )
      return;
    setRequeueErrorPartyId(null);
    requeue.mutate({
      membership: pendingRequeue,
      courtId: selectedCourt.court_id,
    });
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          lg: "minmax(16rem, 20rem) minmax(0, 1fr)",
        },
        gap: 2.5,
      }}
    >
      <Box component="section" aria-label="Find a player" sx={{ minWidth: 0 }}>
        <TextField
          type="search"
          placeholder="Player name"
          size="small"
          fullWidth
          value={search}
          onChange={(event) => changeSearch(event.target.value)}
          slotProps={{
            htmlInput: {
              role: "searchbox",
              "aria-label": "Find a player",
            },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" aria-hidden="true" />
                </InputAdornment>
              ),
            },
          }}
        />

        {playersQuery.isFetching ? (
          <Stack
            spacing={0.5}
            role="status"
            aria-label="Searching players"
            sx={{ mt: 1 }}
          >
            <Skeleton variant="rounded" height={36} />
            <Skeleton variant="rounded" height={36} />
          </Stack>
        ) : playersQuery.isError ? (
          <Stack spacing={1} sx={{ mt: 1, alignItems: "flex-start" }}>
            <Alert severity="error">
              We couldn&apos;t search players. Please try again.
            </Alert>
            <Button
              type="button"
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => void playersQuery.refetch()}
            >
              Retry
            </Button>
          </Stack>
        ) : debouncedSearch.length >= 2 && playersQuery.isSuccess ? (
          playersQuery.data.length ? (
            <List
              component="ul"
              disablePadding
              sx={{
                mt: 1,
                maxHeight: 256,
                overflowY: "auto",
                borderTop: 1,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              {playersQuery.data.map((player) => {
                const name = safeName(player.display_name, player.user_id);
                const isSelected = selectedPlayer?.user_id === player.user_id;
                return (
                  <ListItem
                    key={player.user_id}
                    disablePadding
                    sx={{
                      borderTop: 1,
                      borderColor: "divider",
                      "&:first-of-type": { borderTop: 0 },
                    }}
                  >
                    <Button
                      type="button"
                      color="inherit"
                      fullWidth
                      aria-pressed={isSelected}
                      onClick={() => selectPlayer(player)}
                      sx={{
                        minHeight: 44,
                        justifyContent: "space-between",
                        gap: 1.5,
                        px: 1,
                        textAlign: "left",
                      }}
                    >
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{
                          minWidth: 0,
                          flex: 1,
                          overflowWrap: "anywhere",
                          fontWeight: 600,
                        }}
                      >
                        {name}
                      </Typography>
                      {isSelected ? (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ flexShrink: 0 }}
                        >
                          Selected
                        </Typography>
                      ) : null}
                    </Button>
                  </ListItem>
                );
              })}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              No players found.
            </Typography>
          )
        ) : null}
      </Box>

      <Box component="section" aria-live="polite" sx={{ minWidth: 0 }}>
        {selectedPlayer ? (
          <Box
            sx={{
              minWidth: 0,
              mb: 1.5,
              pb: 1,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Selected player
            </Typography>
            <Typography
              variant="body2"
              sx={{ overflowWrap: "anywhere", fontWeight: 600 }}
            >
              {safeName(selectedPlayer.display_name, selectedPlayer.user_id)}
            </Typography>
          </Box>
        ) : null}
        {!selectedPlayer ? (
          <Typography variant="body2" color="text.secondary">
            Select a player.
          </Typography>
        ) : membershipsQuery.isLoading ? (
          <Stack spacing={1} role="status">
            <Typography variant="body2" color="text.secondary">
              Loading party memberships…
            </Typography>
            <Skeleton variant="rounded" height={80} />
          </Stack>
        ) : membershipsQuery.isError ? (
          <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
            <Alert severity="error">
              We couldn&apos;t load party memberships. Please try again.
            </Alert>
            <Button
              type="button"
              size="small"
              variant="outlined"
              onClick={() => void membershipsQuery.refetch()}
            >
              Retry
            </Button>
          </Stack>
        ) : memberships.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No active party memberships.
          </Typography>
        ) : (
          <List
            component="ul"
            disablePadding
            sx={{
              borderTop: 1,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            {memberships.map((membership) => {
              const createdAt = formatTimestamp(membership.party_created_at);
              const joinedAt = formatTimestamp(membership.membership_joined_at);
              const completedAt = formatTimestamp(
                membership.completed_session_ended_at,
              );
              const canRequeue = membership.can_requeue;
              return (
                <ListItem
                  key={membership.party_id}
                  disableGutters
                  sx={{
                    py: 1.5,
                    alignItems: "flex-start",
                    borderTop: 1,
                    borderColor: "divider",
                    "&:first-of-type": { borderTop: 0 },
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    sx={{
                      width: "100%",
                      minWidth: 0,
                      alignItems: { sm: "flex-start" },
                      justifyContent: "space-between",
                    }}
                  >
                    <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                      <Stack
                        direction="row"
                        useFlexGap
                        spacing={1}
                        sx={{ flexWrap: "wrap", alignItems: "center" }}
                      >
                        <Chip
                          label={partyStatus(membership.party_status)}
                          size="small"
                          variant="outlined"
                        />
                        <Typography variant="caption" color="text.secondary">
                          {linkage(membership)}
                        </Typography>
                        {createdAt ? (
                          <Typography
                            component="time"
                            dateTime={membership.party_created_at}
                            variant="caption"
                            color="text.secondary"
                          >
                            Created {createdAt}
                          </Typography>
                        ) : null}
                        {joinedAt && joinedAt !== createdAt ? (
                          <Typography
                            component="time"
                            dateTime={membership.membership_joined_at}
                            variant="caption"
                            color="text.secondary"
                          >
                            Joined {joinedAt}
                          </Typography>
                        ) : null}
                        {completedAt ? (
                          <Typography
                            component="time"
                            dateTime={
                              membership.completed_session_ended_at ?? undefined
                            }
                            variant="caption"
                            color="text.secondary"
                          >
                            Court session ended {completedAt}
                          </Typography>
                        ) : null}
                      </Stack>

                      <Typography
                        variant="body2"
                        sx={{ overflowWrap: "anywhere", fontWeight: 600 }}
                      >
                        {membership.member_details
                          .map((member) =>
                            safeName(member.display_name, member.user_id),
                          )
                          .join(", ")}
                      </Typography>

                      {membership.integrity_reasons.length ? (
                        <Stack
                          component="ul"
                          aria-label="Issues"
                          direction="row"
                          useFlexGap
                          spacing={1.5}
                          sx={{
                            m: 0,
                            pl: 2,
                            flexWrap: "wrap",
                            color: "error.main",
                          }}
                        >
                          {membership.integrity_reasons.map((reason) => (
                            <Typography
                              component="li"
                              variant="caption"
                              key={reason}
                            >
                              {REASON_LABELS[reason] ??
                                "Membership needs review"}
                            </Typography>
                          ))}
                        </Stack>
                      ) : null}
                    </Stack>

                    {canRequeue || membership.can_remove ? (
                      <Stack
                        spacing={1}
                        sx={{ maxWidth: 224, alignItems: "flex-start" }}
                      >
                        <Stack
                          direction="row"
                          useFlexGap
                          spacing={1}
                          sx={{ flexWrap: "wrap" }}
                        >
                          {canRequeue ? (
                            <Button
                              type="button"
                              size="small"
                              variant="outlined"
                              disabled={requeue.isPending || cleanup.isPending}
                              onClick={() => {
                                setRequeueErrorPartyId(null);
                                if (
                                  membership.queue_assignment_mode ===
                                  "self_serve"
                                ) {
                                  setTargetCourtId("");
                                  setCourtPickerOpen(false);
                                  setPendingRequeue(membership);
                                  return;
                                }
                                requeue.mutate({ membership });
                              }}
                            >
                              {requeue.isPending &&
                              requeue.variables?.membership.party_id ===
                                membership.party_id
                                ? "Adding…"
                                : "Add party to queue"}
                            </Button>
                          ) : null}
                          {membership.can_remove ? (
                            <Button
                              type="button"
                              size="small"
                              variant="contained"
                              color="error"
                              disabled={requeue.isPending || cleanup.isPending}
                              onClick={() => {
                                cleanup.reset();
                                setRemovalError(null);
                                setPendingRemoval(membership);
                              }}
                            >
                              {membership.is_leader
                                ? "Disband party"
                                : "Remove from party"}
                            </Button>
                          ) : null}
                        </Stack>
                        {requeueErrorPartyId === membership.party_id ? (
                          <Typography
                            variant="caption"
                            color="error"
                            role="alert"
                          >
                            We couldn&apos;t add this party. Review the latest
                            queue and party status before trying again.
                          </Typography>
                        ) : null}
                      </Stack>
                    ) : (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ maxWidth: 224 }}
                      >
                        {membership.entry_id || membership.assignment_id
                          ? "Finish the active queue or court session first"
                          : "Requeue is currently unavailable"}
                      </Typography>
                    )}
                  </Stack>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>

      <Dialog
        open={pendingRemoval !== null}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {pendingRemoval?.is_leader
            ? "Disband party?"
            : "Remove party membership?"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <DialogContentText sx={{ overflowWrap: "anywhere" }}>
              {pendingRemoval?.is_leader
                ? `Disband this party for ${
                    selectedPlayer
                      ? safeName(
                          selectedPlayer.display_name,
                          selectedPlayer.user_id,
                        )
                      : "this player"
                  } and every member. ${membershipContext(pendingRemoval)}`
                : `Remove ${
                    selectedPlayer
                      ? safeName(
                          selectedPlayer.display_name,
                          selectedPlayer.user_id,
                        )
                      : "this player"
                  } from this party. ${
                    pendingRemoval ? membershipContext(pendingRemoval) : ""
                  }`}
            </DialogContentText>
            {removalError ? (
              <Alert severity="error">{removalError}</Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            type="button"
            size="small"
            variant="outlined"
            onClick={closeDialog}
            disabled={cleanup.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="small"
            variant="contained"
            color="error"
            onClick={confirmRemoval}
            disabled={cleanup.isPending}
          >
            {cleanup.isPending
              ? pendingRemoval?.is_leader
                ? "Disbanding…"
                : "Removing…"
              : pendingRemoval?.is_leader
                ? "Disband party"
                : "Remove membership"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pendingRequeue !== null}
        onClose={closeRequeueDialog}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: { sx: { maxHeight: "calc(100dvh - 2rem)" } },
        }}
      >
        <DialogTitle>Choose a court</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <DialogContentText sx={{ overflowWrap: "anywhere" }}>
              Select the court line this party should join.
              {pendingRequeue ? ` ${membershipContext(pendingRequeue)}` : ""}
            </DialogContentText>

            {courtBoardQuery.isFetching ? (
              <Stack spacing={1} role="status">
                <Typography variant="body2" color="text.secondary">
                  Loading courts…
                </Typography>
                <Skeleton variant="rounded" height={40} />
              </Stack>
            ) : courtBoardQuery.isError ? (
              <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
                <Alert severity="error">
                  We couldn&apos;t load the court board. Please try again.
                </Alert>
                <Button
                  type="button"
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => void courtBoardQuery.refetch()}
                >
                  Retry
                </Button>
              </Stack>
            ) : courtBoardQuery.data?.queue_open === false ? (
              <Alert severity="warning">
                The drop-in queue is closed. Reopen it before adding this party.
              </Alert>
            ) : eligibleCourts.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No available court can fit this party.
              </Typography>
            ) : (
              <Autocomplete
                id="party-requeue-court"
                open={courtPickerOpen}
                onOpen={() => setCourtPickerOpen(true)}
                onClose={() => setCourtPickerOpen(false)}
                options={eligibleCourts}
                value={selectedCourt ?? null}
                getOptionLabel={courtOptionLabel}
                isOptionEqualToValue={(option, value) =>
                  option.court_id === value.court_id
                }
                onChange={(_event, court) =>
                  setTargetCourtId(court?.court_id ?? "")
                }
                disabled={requeue.isPending || courtBoardQuery.isFetching}
                noOptionsText="No courts found."
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Court"
                    placeholder="Choose court"
                    slotProps={{
                      ...params.slotProps,
                      htmlInput: {
                        ...params.slotProps.htmlInput,
                        "aria-label": "Court",
                      },
                    }}
                  />
                )}
                slotProps={{
                  listbox: {
                    sx: {
                      "& .MuiAutocomplete-option": {
                        alignItems: "flex-start",
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                      },
                    },
                  },
                }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            type="button"
            size="small"
            variant="outlined"
            onClick={closeRequeueDialog}
            disabled={requeue.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="small"
            variant="contained"
            onClick={confirmRequeue}
            disabled={
              !selectedCourt ||
              requeue.isPending ||
              courtBoardQuery.isFetching ||
              courtBoardQuery.isError ||
              courtBoardQuery.data?.queue_open === false
            }
          >
            {requeue.isPending ? "Adding…" : "Add to court line"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
